// GENERATE ONE LEVEL'S AREAS AND SYSTEMS INTO THE SCHEDULE
// (back/utils/levelScheduleGen; owner, 2026-10-26: "a dropdown per page/level
// that tages all areas and systems, in that page/level to a level from the
// schedule, which will automatically generate these areas/ and their systems,
// in the schedule, if they don't exist with a button that says generate
// areas/systems, for all levelX activity, on all schedule tree children that
// have that level/page assigned to them").
//
// THE DIALOG IS A BEFORE AND AFTER, because that is what he asked for: "a
// confirmation poup will show up to show what the each child in the schedule
// looks like now, their children and what applying the areas and systems,
// will make the schedule look like". So each target activity is drawn twice,
// side by side - the children it has today, and the children it would have -
// rather than as a list of additions, which would answer a different
// question.
//
// Nothing is written until Generate, and the server recomputes the plan then:
// what this shows is never what gets written.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Checkbox, Empty, Modal, Spin, Tag } from "antd";
import { previewLevelAreas, applyLevelAreas, undoLevelAreas } from "api/gantt/ganttApi";

const STATE = {
  new: { color: "green", text: "new" },
  adopted: { color: "blue", text: "already there" },
};
const dates = (r) => (r.start || r.end ? `${r.start || "?"} → ${r.end || "?"}` : "no dates");

// One side of one target: the rows, each with its children.
export function TreeSide({ rows, empty, onTick, ticked }) {
  if (!rows || !rows.length) return <div style={{ color: "#94a3b8", fontSize: 12 }}>{empty}</div>;
  return (
    <ul style={{ listStyle: "none", paddingLeft: 0, marginBottom: 0 }}>
      {rows.map((r) => (
        <li key={r.id || r.name} style={{ marginBottom: 4 }}>
          <div>
            {r.state ? <Tag color={(STATE[r.state] || {}).color}>{(STATE[r.state] || {}).text || r.state}</Tag> : null}
            <b>{r.name}</b> <span style={{ color: "#64748b", fontSize: 12 }}>{dates(r)}</span>
          </div>
          {(r.flags || []).map((f) => (
            <div key={f} style={{ fontSize: 11.5, color: "#b45309", marginLeft: 12 }}>
              {f}
              {/* The one decision in this dialog: an activity already there
                  keeps its own dates unless it is ticked. */}
              {onTick && r.state === "adopted" && /equal share/.test(f) ? (
                <Checkbox
                  style={{ marginLeft: 8 }}
                  checked={!!ticked && ticked.includes(r.id)}
                  onChange={(e) => onTick(r.id, e.target.checked)}
                >
                  re-date it
                </Checkbox>
              ) : null}
            </div>
          ))}
          {(r.children || []).length > 0 && (
            <ul style={{ listStyle: "none", paddingLeft: 16, marginBottom: 0 }}>
              {r.children.map((c) => (
                <li key={c.id || c.name} style={{ fontSize: 12.5 }}>
                  {c.state ? <Tag color={(STATE[c.state] || {}).color}>{(STATE[c.state] || {}).text || c.state}</Tag> : null}
                  {c.name} <span style={{ color: "#64748b" }}>{dates(c)}</span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

// A one-line summary of a plan, for the button's own tooltip and the result.
export function summarise(counts) {
  const c = counts || {};
  const bits = [];
  if (c.areasCreated) bits.push(`${c.areasCreated} area${c.areasCreated === 1 ? "" : "s"}`);
  if (c.systemsCreated) bits.push(`${c.systemsCreated} system task${c.systemsCreated === 1 ? "" : "s"}`);
  if (c.redated) bits.push(`${c.redated} re-dated`);
  if (!bits.length) return "nothing to add";
  return `${bits.join(", ")} across ${c.targets || 0} activit${(c.targets || 0) === 1 ? "y" : "ies"}`;
}

export default function LevelAreasBuilder({ projectId, pageId, pageName, level, onClose, onDone }) {
  const [state, setState] = useState({ loading: true, plan: null, error: "" });
  const [redate, setRedate] = useState([]);
  const [onlyReady, setOnlyReady] = useState(false);
  // WHICH ACTIVITIES. null means "every one on this level", which is what the
  // owner asked for and so what this opens on. A real schedule puts a level
  // on the level row AND on each of its milestones, so on spaceX-test that
  // is 61 activities and 122 area rows from one press - hence the count in
  // plain sight and the one-click way down to the top-most rows.
  const [only, setOnly] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const load = useCallback(async (opts) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const plan = await previewLevelAreas(projectId, {
        pageId, level, redate: opts.redate, onlyReady: opts.onlyReady, only: opts.only || [],
      });
      if (plan && plan.err) throw new Error(plan.err);
      setState({ loading: false, plan, error: "" });
    } catch (e) {
      setState({ loading: false, plan: null, error: e.message || "Could not work out what to generate" });
    }
  }, [projectId, pageId, level]);

  // A PREVIEW IS A WHOLE-SCHEDULE READ (the server loads every activity on
  // the project to work the plan out), and ticking through a list of sixty
  // activities would ask for one per tick. Settle first.
  useEffect(() => {
    const t = setTimeout(() => load({ redate, onlyReady, only }), 250);
    return () => clearTimeout(t);
  }, [load, redate, onlyReady, only]);

  const plan = state.plan;
  const run = async () => {
    setBusy(true);
    try {
      const r = await applyLevelAreas(projectId, {
        pageId, level, redate, onlyReady, only: only || [], planHash: plan && plan.planHash,
      });
      if (r && r.err) {
        // A refusal carries the fresh plan: show THAT, never the stale one.
        if (r.plan) setState({ loading: false, plan: r.plan, error: r.err });
        else setState((s) => ({ ...s, error: r.err }));
        return;
      }
      setResult(r);
      if (onDone) onDone(r);
    } catch (e) {
      setState((s) => ({ ...s, error: e.message || "Could not generate" }));
    } finally { setBusy(false); }
  };

  const undo = async () => {
    setUndoing(true);
    try {
      const r = await undoLevelAreas(projectId, result.runId);
      if (r && r.err) { setState((s) => ({ ...s, error: r.err })); return; }
      setResult({ ...result, undone: true });
      if (onDone) onDone(r);
    } finally { setUndoing(false); }
  };

  const tick = (id, on) => setRedate((s) => (on ? [...new Set([...s, id])] : s.filter((x) => x !== id)));

  return (
    <Modal
      open
      title={`Generate areas & systems - ${pageName} → schedule level "${level}"`}
      onCancel={onClose}
      width={1040}
      className="integration-settings-modal"
      destroyOnClose
      footer={result ? (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {result.runId && !result.undone ? <Button onClick={undo} loading={undoing}>Undo</Button> : null}
          <Button type="primary" onClick={onClose}>Close</Button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={busy}
            disabled={!plan || !!plan.refused || plan.nothingToDo}
            onClick={run}
          >
            Generate
          </Button>
        </div>
      )}
    >
      {result ? (
        <Alert
          type={result.undone ? "info" : "success"}
          showIcon
          message={result.undone ? "Put back" : "Done"}
          description={result.undone
            ? "The schedule is as it was before this generation."
            : `${result.created} activit${result.created === 1 ? "y" : "ies"} added${result.updated ? `, ${result.updated} re-dated` : ""}. ${summarise(result.counts)}.`}
        />
      ) : null}

      {state.error ? <Alert type="error" showIcon message={state.error} style={{ marginBottom: 10 }} /> : null}
      {state.loading ? <div style={{ padding: 24, textAlign: "center" }}><Spin /></div> : null}

      {!result && plan && plan.refused ? (
        <Alert
          type="warning"
          showIcon
          message={plan.refused.text}
          action={plan.refused.code === "partial" ? (
            <Button size="small" onClick={() => setOnlyReady(true)}>Generate the ready areas only</Button>
          ) : null}
        />
      ) : null}

      {!result && plan && !plan.refused ? (
        <div>
          <div style={{ marginBottom: 8, color: "#475569", fontSize: 12.5 }}>
            {summarise(plan.counts)}. Each activity&apos;s working days are shared equally between its areas, and each
            area&apos;s between its systems - the earliest take the remainder. Nothing already there is changed or removed.
            {plan.calendar ? ` Calendar: ${plan.calendar.name}.` : ""}
          </div>
          {(plan.available || []).length > 1 && (
            <div style={{ marginBottom: 8, fontSize: 12.5 }} data-testid="level-areas-choose">
              <b>{plan.available.length}</b> activit{plan.available.length === 1 ? "y is" : "ies are"} on this level
              {plan.counts.nested
                ? <> - <b>{plan.counts.nested}</b> of them sit under another one, so both rungs would get a copy of every area.</>
                : "."}
              {" "}
              <Button
                size="small"
                type="link"
                style={{ padding: 0 }}
                onClick={() => setOnly(plan.available.filter((a) => !a.nested).map((a) => a.id))}
              >
                Top-most rows only ({plan.available.filter((a) => !a.nested).length})
              </Button>
              {only ? (
                <Button size="small" type="link" style={{ padding: 0, marginLeft: 8 }} onClick={() => setOnly(null)}>
                  All {plan.available.length}
                </Button>
              ) : null}
              <div style={{ marginTop: 4, maxHeight: 120, overflowY: "auto" }}>
                {plan.available.map((a) => (
                  <Checkbox
                    key={a.id}
                    style={{ display: "block", marginLeft: a.nested ? 16 : 0 }}
                    checked={!only || only.includes(a.id)}
                    onChange={(e) => {
                      const base = only || plan.available.map((x) => x.id);
                      setOnly(e.target.checked ? [...new Set([...base, a.id])] : base.filter((x) => x !== a.id));
                    }}
                  >
                    <span style={{ fontSize: 12.5 }}>{a.name}{a.nested ? <span style={{ color: "#94a3b8" }}> (under another activity on this level)</span> : null}</span>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}
          {plan.counts.overflow ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={`${plan.counts.overflow} task${plan.counts.overflow === 1 ? "" : "s"} would run past its parent's finish - there are more areas or systems than the parent has working days.`}
            />
          ) : null}
          {(plan.skipped || []).length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 12, color: "#64748b" }} data-testid="level-areas-skipped">
              Left alone: {plan.skipped.map((s) => `${s.name} (${s.why})`).join("; ")}
            </div>
          )}
          {!plan.targets.length ? <Empty description="No activity on this level" /> : null}
          {plan.targets.map((t) => (
            <div key={t.id} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, marginBottom: 10 }} data-testid="level-areas-target">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {t.name} <span style={{ color: "#64748b", fontWeight: 400, fontSize: 12 }}>{dates(t)}</span>
              </div>
              {t.note ? <div style={{ fontSize: 12, color: "#b45309", marginBottom: 4 }}>{t.note}</div> : null}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 320px", minWidth: 0 }} data-testid="level-areas-now">
                  <div style={{ fontSize: 11.5, textTransform: "uppercase", color: "#94a3b8", marginBottom: 4 }}>Now</div>
                  <TreeSide rows={t.before} empty="No children" />
                </div>
                <div style={{ flex: "1 1 320px", minWidth: 0 }} data-testid="level-areas-after">
                  <div style={{ fontSize: 11.5, textTransform: "uppercase", color: "#94a3b8", marginBottom: 4 }}>After generating</div>
                  <TreeSide rows={t.after} empty="Nothing to add" onTick={tick} ticked={redate} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Modal>
  );
}
